/**
 * 레거시 `ruda-admin-web/components/admin/userStatusTag.js` 포팅.
 * 유저 목록·상세에서 상태 값을 색상 태그로 표시한다.
 */
import type { UserContractInfo } from '@/entities/contract/api/consignment-contract.api';
import { BUSINESS_OWNER_TYPE } from '@/entities/user/api/user.api';
import { rdsGreen } from '@/shared/styles/rds-colors';
import {
  CONTRACT_DISPLAY_STATUS,
  CONTRACT_DISPLAY_STATUS_LABEL,
  getContractDisplayStatusLabel,
  isContractCompletedDisplayStatus,
  isNotContractedDisplayStatus,
  normalizeContractDisplayStatus,
} from '@/pages/users/contractDisplayStatus';
import React from 'react';

const TAG_BASE_CLASS =
  'inline-flex items-center rounded-lg px-2 py-2 text-[13px] leading-none';

const TAG_COLORS = {
  gray: { backgroundColor: '#b1b1b1', color: '#f5f5f5' },
  red: { backgroundColor: '#ff0000', color: '#f5f5f5' },
  blue: { backgroundColor: '#499df1', color: '#f5f5f5' },
  pink: { backgroundColor: '#ff9292', color: '#f5f5f5' },
  darkGray: { backgroundColor: '#818181', color: '#f5f5f5' },
  green: { backgroundColor: rdsGreen[500], color: '#f5f5f5' },
} as const;

type TagColorKey = keyof typeof TAG_COLORS;

type TagConfig = { label: string; color: TagColorKey };

const StatusTag: React.FC<TagConfig> = ({ label, color }) => (
  <span className={TAG_BASE_CLASS} style={TAG_COLORS[color]}>
    {label}
  </span>
);

const PROMO_LICENSE_STATUS_TAG_MAP: Record<string, TagConfig> = {
  NOT_REGISTERED: { label: '미등록', color: 'gray' },
  PENDING: { label: '확인 전', color: 'red' },
  APPROVED: { label: '승인', color: 'blue' },
  REJECTED: { label: '승인불가', color: 'pink' },
  EXPIRED: { label: '만료', color: 'pink' },
};

const BUSINESS_VERIFICATION_STATUS_TAG_MAP: Record<string, TagConfig> = {
  NOT_REGISTERED: { label: '미등록', color: 'gray' },
  PENDING: { label: '확인 전', color: 'red' },
  APPROVED: { label: '승인', color: 'blue' },
  REJECTED: { label: '승인불가', color: 'pink' },
  BUSINESS_TYPE_NOT_ALLOWED: { label: '업종업태불가', color: 'pink' },
  EXPIRED: { label: '만료', color: 'pink' },
};

const FINANCIAL_VERIFICATION_STATUS_TAG_MAP: Record<string, TagConfig> = {
  NOT_REGISTERED: { label: '인증 전', color: 'gray' },
  NOT_VERIFIED: { label: '인증 전', color: 'gray' },
  VERIFICATION_FAILED: { label: '인증 전', color: 'gray' },
  VERIFIED: { label: '인증 완료', color: 'blue' },
};

const SALES_APPROVAL_STATUS_TAG_MAP: Record<string, TagConfig> = {
  TRANSACTION_IMPOSSIBLE: { label: '거래 신청 불가', color: 'gray' },
  TRANSACTION_POSSIBLE: { label: '거래 신청 가능', color: 'darkGray' },
  SETTLEMENT_POSSIBLE: { label: '정산 가능', color: 'blue' },
};

const CONTRACT_STATUS_TAG_MAP: Record<string, TagConfig> = {
  [CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED]: {
    label:
      CONTRACT_DISPLAY_STATUS_LABEL[CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED],
    color: 'gray',
  },
  [CONTRACT_DISPLAY_STATUS.SIGNING_REVIEW]: {
    label:
      CONTRACT_DISPLAY_STATUS_LABEL[CONTRACT_DISPLAY_STATUS.SIGNING_REVIEW],
    color: 'blue',
  },
  [CONTRACT_DISPLAY_STATUS.PENDING_REVIEW]: {
    label:
      CONTRACT_DISPLAY_STATUS_LABEL[CONTRACT_DISPLAY_STATUS.PENDING_REVIEW],
    color: 'red',
  },
  [CONTRACT_DISPLAY_STATUS.RECONTRACT_REQUESTED]: {
    label:
      CONTRACT_DISPLAY_STATUS_LABEL[
        CONTRACT_DISPLAY_STATUS.RECONTRACT_REQUESTED
      ],
    color: 'pink',
  },
  [CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED]: {
    label:
      CONTRACT_DISPLAY_STATUS_LABEL[CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED],
    color: 'red',
  },
  [CONTRACT_DISPLAY_STATUS.EXTERNAL_CONTRACT]: {
    label:
      CONTRACT_DISPLAY_STATUS_LABEL[CONTRACT_DISPLAY_STATUS.EXTERNAL_CONTRACT],
    color: 'darkGray',
  },
};

const ACCOUNT_STATUS_TAG_MAP: Record<string, TagConfig> = {
  ACTIVE: { label: '정상', color: 'blue' },
  WITHDRAWN: { label: '탈퇴', color: 'gray' },
  DELETED: { label: '삭제', color: 'gray' },
};

/** 목록 스펙: 미지정. 상세 Select 의 미등록과 라벨이 다르다 */
const BUSINESS_OWNER_TYPE_TAG_FALLBACK: TagConfig = {
  label: '미지정',
  color: 'gray',
};

const BUSINESS_OWNER_TYPE_TAG_MAP: Record<string, TagConfig> = {
  [BUSINESS_OWNER_TYPE.INDIVIDUAL]: { label: '개인', color: 'green' },
  [BUSINESS_OWNER_TYPE.CORPORATE]: { label: '법인', color: 'blue' },
};

const renderMappedStatusTag = (
  statusMap: Record<string, TagConfig>,
  status: string | undefined | null,
  fallback: TagConfig,
) => {
  const config = (status && statusMap[status]) || fallback;
  return <StatusTag {...config} />;
};

export const PromoLicenseStatusTag: React.FC<{ status?: string | null }> = ({
  status,
}) =>
  renderMappedStatusTag(
    PROMO_LICENSE_STATUS_TAG_MAP,
    status,
    PROMO_LICENSE_STATUS_TAG_MAP.NOT_REGISTERED,
  );

export const BusinessVerificationStatusTag: React.FC<{
  status?: string | null;
}> = ({ status }) =>
  renderMappedStatusTag(
    BUSINESS_VERIFICATION_STATUS_TAG_MAP,
    status,
    BUSINESS_VERIFICATION_STATUS_TAG_MAP.NOT_REGISTERED,
  );

export const FinancialVerificationStatusTag: React.FC<{
  status?: string | null;
}> = ({ status }) =>
  renderMappedStatusTag(
    FINANCIAL_VERIFICATION_STATUS_TAG_MAP,
    status,
    FINANCIAL_VERIFICATION_STATUS_TAG_MAP.NOT_VERIFIED,
  );

export const SalesApprovalStatusTag: React.FC<{ status?: string | null }> = ({
  status,
}) =>
  renderMappedStatusTag(
    SALES_APPROVAL_STATUS_TAG_MAP,
    status,
    SALES_APPROVAL_STATUS_TAG_MAP.TRANSACTION_IMPOSSIBLE,
  );

export type ContractStatusRecord = {
  contractInfo?: UserContractInfo | null;
};

export const ContractStatusTag: React.FC<{ record: ContractStatusRecord }> = ({
  record,
}) => {
  const contractInfo = record.contractInfo ?? null;

  if (contractInfo == null) {
    return (
      <StatusTag
        {...CONTRACT_STATUS_TAG_MAP[CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED]}
      />
    );
  }

  const rawStatus = String(contractInfo.contractStatus ?? '').trim();
  if (!rawStatus && isNotContractedDisplayStatus(rawStatus)) {
    return (
      <StatusTag
        {...CONTRACT_STATUS_TAG_MAP[CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED]}
      />
    );
  }

  const statusKey =
    normalizeContractDisplayStatus(rawStatus) ??
    (isContractCompletedDisplayStatus(rawStatus, contractInfo.reviewStatus)
      ? CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED
      : null);

  if (statusKey && CONTRACT_STATUS_TAG_MAP[statusKey]) {
    return <StatusTag {...CONTRACT_STATUS_TAG_MAP[statusKey]} />;
  }

  return (
    <StatusTag
      label={getContractDisplayStatusLabel(rawStatus)}
      color="darkGray"
    />
  );
};

export type AccountStatusRecord = {
  status?: string | null;
  withdrawnAt?: string | null;
};

export const getAccountStatusFromRecord = (
  record: AccountStatusRecord,
): keyof typeof ACCOUNT_STATUS_TAG_MAP => {
  const status = String(record.status || '').toUpperCase();
  if (status === 'DELETED') return 'DELETED';
  if (record.withdrawnAt || status === 'WITHDRAWN') return 'WITHDRAWN';
  return 'ACTIVE';
};

const statusLabel = (
  statusMap: Record<string, TagConfig>,
  status: string | undefined | null,
  fallback: TagConfig,
): string =>
  status && statusMap[status] ? statusMap[status].label : fallback.label;

export const getPromoLicenseStatusLabel = (status?: string | null): string =>
  statusLabel(
    PROMO_LICENSE_STATUS_TAG_MAP,
    status,
    PROMO_LICENSE_STATUS_TAG_MAP.NOT_REGISTERED,
  );

export const getBusinessVerificationStatusLabel = (
  status?: string | null,
): string =>
  statusLabel(
    BUSINESS_VERIFICATION_STATUS_TAG_MAP,
    status,
    BUSINESS_VERIFICATION_STATUS_TAG_MAP.NOT_REGISTERED,
  );

export const getFinancialVerificationStatusLabel = (
  status?: string | null,
): string =>
  statusLabel(
    FINANCIAL_VERIFICATION_STATUS_TAG_MAP,
    status,
    FINANCIAL_VERIFICATION_STATUS_TAG_MAP.NOT_VERIFIED,
  );

export const getSalesApprovalStatusLabel = (status?: string | null): string =>
  statusLabel(
    SALES_APPROVAL_STATUS_TAG_MAP,
    status,
    SALES_APPROVAL_STATUS_TAG_MAP.TRANSACTION_IMPOSSIBLE,
  );

export const getContractStatusLabelFromRecord = (
  record: ContractStatusRecord,
): string => {
  const contractInfo = record.contractInfo ?? null;

  if (contractInfo == null) {
    return CONTRACT_STATUS_TAG_MAP[CONTRACT_DISPLAY_STATUS.NOT_CONTRACTED]
      .label;
  }

  const rawStatus = String(contractInfo.contractStatus ?? '').trim();
  const statusKey =
    normalizeContractDisplayStatus(rawStatus) ??
    (isContractCompletedDisplayStatus(rawStatus, contractInfo.reviewStatus)
      ? CONTRACT_DISPLAY_STATUS.CONTRACT_COMPLETED
      : null);

  if (statusKey && CONTRACT_STATUS_TAG_MAP[statusKey]) {
    return CONTRACT_STATUS_TAG_MAP[statusKey].label;
  }

  return getContractDisplayStatusLabel(rawStatus);
};

export const getAccountStatusLabel = (record: AccountStatusRecord): string =>
  ACCOUNT_STATUS_TAG_MAP[getAccountStatusFromRecord(record)].label;

export const AccountStatusTag: React.FC<{ record: AccountStatusRecord }> = ({
  record,
}) => (
  <StatusTag {...ACCOUNT_STATUS_TAG_MAP[getAccountStatusFromRecord(record)]} />
);

export const BusinessOwnerTypeTag: React.FC<{
  type?: string | null;
}> = ({ type }) =>
  renderMappedStatusTag(
    BUSINESS_OWNER_TYPE_TAG_MAP,
    type,
    BUSINESS_OWNER_TYPE_TAG_FALLBACK,
  );
